import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { 
  Building2, 
  ArrowRight,
  Handshake,
  FileText,
  Users,
} from "lucide-react";
import type { Company, CompanyToCompany } from "@shared/schema";

interface ProxyRelationshipWithCompanies {
  id: string;
  companyAId: string;
  companyBId: string;
  proxyStatus: string | null;
  clientCompany: Company;
  agencyCompany: Company;
}

export default function UserClients() {
  const { data: proxyRelationships = [], isLoading } = useQuery<ProxyRelationshipWithCompanies[]>({
    queryKey: ["/api/proxy/relationships"],
  });

  const activeRelationships = proxyRelationships.filter(r => r.proxyStatus === "active");

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" data-testid="text-clients-title">Client Companies</h1>
        <p className="text-muted-foreground">
          Companies you can create content for through proxy relationships
        </p>
      </div>

      {activeRelationships.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Handshake className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Client Relationships</h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              When your company establishes proxy relationships with clients, they will appear here. 
              You'll be able to create content on their behalf.
            </p>
            <Link href="/dashboard/affiliations">
              <Button variant="outline">
                View My Affiliations
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {activeRelationships.map((relationship) => (
            <Card key={relationship.id} data-testid={`card-client-${relationship.id}`}>
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10">
                    <Building2 className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base">{relationship.clientCompany?.name || "Client Company"}</CardTitle>
                    <CardDescription className="text-xs">
                      Via {relationship.agencyCompany?.name || "Your Agency"}
                    </CardDescription>
                  </div>
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                    Active
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    <span>Content posting enabled</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link href={`/dashboard/press-releases?client=${relationship.companyBId}`} className="flex-1">
                    <Button variant="outline" className="w-full" data-testid={`button-create-for-${relationship.id}`}>
                      Create Content
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-5 w-5" />
            How Proxy Relationships Work
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Proxy relationships allow your agency to create and submit content on behalf of client companies.
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Both companies must approve the relationship</li>
            <li>Content is clearly attributed: "Posted by [You] via [Agency] on behalf of [Client]"</li>
            <li>Either party can revoke the relationship at any time</li>
          </ul>
          <p className="text-xs">
            Contact your company administrator to establish new client relationships.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
